import React, { FC, useEffect, useState } from 'react';
import './ChallengeForm.css';
import {
	Toast,
	SubmitButton,
	PageLoader,
	IoCloseCircleSharp,
	IoAccessibility,
	FaRobot,
	useToast
} from '@/Shared';


import { useEnigmaForm, usePinEntryChallenge } from '../../Logic/Hooks';

interface ChallengeFormProps { }


/**
 * =====================================================
 * Component: ChallengeForm
 * =====================================================
 *
 * React component for displaying and handling a user challenge (captcha/enigma) form.
 *
 * Responsibilities:
 * - Show challenge digits and track user input
 * - Enable/disable submit button based on input validity
 * - Provide real-time feedback via toast notifications
 * - Handle challenge success, failure, and blocked states
 * - Navigate users according to challenge outcome
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
const ChallengeForm: FC<ChallengeFormProps> = () => {

	/**
	 * =====================================================
	 * Hooks and Form Utilities
	 * =====================================================
	 *
	 * - toastHook: manages toast notifications
	 * - usePinEntryChallenge: manages challenge digits input
	 * - useEnigmaForm: handles form submission and challenge response
	 * - formVM: current ChallengeFormViewModel
	 */
	const toastHook = useToast();

	const {
		enigmas,
		digits,
		isFullfilled,
		positions,
		addDigit,
		clearDigits
	} = usePinEntryChallenge();

	const { handleEnigma, enigmaResponse, entityFormVM } = useEnigmaForm(toastHook, clearDigits);
	

	/**
	 * =====================================================
	 * Local Component State
	 * =====================================================
	 *
	 * - transitionClass: CSS class for animation on digit input
	 * - enableSubmit: controls SubmitButton enablement
	 */
	const [transitionClass, setTransitionClass] = useState('');
	const [enableSubmit, setEnableSubmit] = useState<boolean>(false);


	 /**
	 * =====================================================
	 * Effect: Update UI transitions and submit button state
	 * =====================================================
	 *
	 * Watches:
	 * - digits input completion (isFullfilled)
	 * - formVM status (blocked/loading)
	 *
	 * Behavior:
	 * - Adds/removes CSS transition class
	 * - Enables submit button only if input is complete and challenge is active
	 */
	useEffect(() => {
		const timer = setTimeout(() => {
			setTransitionClass(isFullfilled ? 'deactivate' : '');
			setEnableSubmit(
				isFullfilled
				&& !entityFormVM?.isBlocked()
				&& !entityFormVM?.isLoading(enigmaResponse.status)
			);

		}, 100);

		return () => clearTimeout(timer); // nettoyage propre

	}, [isFullfilled, entityFormVM]);


	/**
	 * =====================================================
	 * Loading State
	 * =====================================================
	 *
	 * Displays loader while challenge form is initializing
	 */
	if (!entityFormVM) {
		return <PageLoader message='Captcha en cours de chargement...' />
	}


	 /**
	 * =====================================================
	 * Render
	 * =====================================================
	 *
	 * Layout:
	 * - Header: shows challenge title, entered digits, and clear button
	 * - Body: displays clickable digits for user input
	 * - Footer: submit button with dynamic label and icon
	 */
	return (
		<div className="ChallengeForm" data-testid="ChallengeForm">

			<div className='ChallengeForm__header'>
				<Toast
					data={toastHook.toastData}
					onClose={toastHook.closeToast}
				/>
				<div className='enigma-advice'>{entityFormVM.title}</div>

				<div className='enigma-result'>
					<span className='enigma-result__value'>{digits}</span>
					<span className='enigma-result__icon' onClick={clearDigits}>
						<IoCloseCircleSharp />
					</span>
				</div>
			</div>
			<div className={`ChallengeForm__body ${transitionClass}`}>{
				enigmas.map(
					(digit, index) => (
						<div 
							className='ChallengeForm__digit' 
							key={digit} 
							onClick={() => addDigit(index)}>
							{digit}
						</div>
					)
				)
			}
			</div>
			<div className='ChallengeForm__footer'>
				<SubmitButton
					label={enableSubmit ? 'Je suis un humain.' : 'Etes-vous un robot?'}
					disabled={!enableSubmit}
					onClick={() => { handleEnigma(entityFormVM.id, positions) }}
					isLoading={entityFormVM.isLoading(enigmaResponse.status)}
					icon={enableSubmit ? <IoAccessibility /> : <FaRobot />}
					
				/>
			</div>
		</div>
	);
}

export default ChallengeForm;